import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Heading } from '@chakra-ui/react';
import {
  OccupancyType,
  PageType,
  StudentSelectionMode,
  SystemTranslationKeys,
} from '@common/entities';
import { RootState } from 'state/store';

import { useSystemTranslation } from 'hooks';
import { useStudentSelectionMode } from 'hooks/floor-plans';

import { NoUnitAvailable } from './NoUnitAvailable';
import { SelectedUnitDetails } from './SelectedUnitDetails';
import WaitListCard from './WaitListCard';

interface Props {
  handleDrawerClose?: () => void;
}

export function NewHomeCard({ handleDrawerClose }: Props) {
  const { tSystem } = useSystemTranslation();
  const { unit, floorPlan, waitlistSelections } = useSelector(
    (state: RootState) => state.unitSelection,
  );
  const { availableSpaceConfigurationIds, selectedLeaseTermId } = useSelector(
    (state: RootState) => state.studentUnitSelection,
  );
  const { occupancyType, pages } = useSelector((state: RootState) => state.application.form);
  const studentSelectionMode = useStudentSelectionMode();

  // Check if we're in student bulk assignment mode with valid selection
  const isStudentBulkMode =
    occupancyType === OccupancyType.Student &&
    studentSelectionMode === StudentSelectionMode.BULK_UNIT_ASSIGNMENT;

  const hasBulkSelection =
    isStudentBulkMode &&
    floorPlan &&
    availableSpaceConfigurationIds.length > 0 &&
    selectedLeaseTermId;

  const hasFloorPlansPage =
    Array.isArray(pages) && pages.some((p) => p.type === PageType.FloorPlans);
  const hasSelectedHome = Boolean(
    unit || hasBulkSelection || waitlistSelections?.floorplanIds?.length,
  );

  if (!hasSelectedHome && !hasFloorPlansPage) {
    return null;
  }

  return (
    <Box mb={5}>
      <Heading as="h4" size="md" letterSpacing="0.5px">
        {tSystem(SystemTranslationKeys.cart.yourNewHome)}
      </Heading>
      {(() => {
        if (unit || hasBulkSelection) {
          return <SelectedUnitDetails />;
        }
        if ((waitlistSelections?.floorplanIds?.length ?? 0) > 0) {
          return <WaitListCard />;
        }
        return (
          <NoUnitAvailable
            handleDrawerClose={handleDrawerClose}
            selectFloorplan={isStudentBulkMode}
          />
        );
      })()}
    </Box>
  );
}
